import { useCallback } from "react";
import { FormikProps } from "formik";
import { extractUrlFromEmbed } from "../utils";

interface UseEmbedMediaOperationsProps {
  formik: FormikProps<{
    Embed_Url: string;
    AssetType: string;
    Streaming: string;
    EmbedType: string;
  }>;
  fromDetails?: boolean;
  currentMediaDetailsForEdit?: any;
  getUpdateMedia: (data: any, shouldUpdate?: boolean) => void;
  callRegisterCustomMediaOnCms: (
    data: any,
    callback?: (resp: any) => void
  ) => any;
  setMediaDetails: (data: { data: { media: any } }) => any;
  updateIsClearFilter: (value: boolean) => any;
  updateIsFilter: (value: boolean) => any;
  toast?: any;
  onEmbedUpdated?: (id: any) => void;
  closeModal: () => void;
  onMediaMetaSave?: (mediaId: string) => void;
  setLoading?: (loading: boolean) => void;
}

export const useEmbedMediaOperations = ({
  formik,
  fromDetails = false,
  currentMediaDetailsForEdit,
  getUpdateMedia,
  callRegisterCustomMediaOnCms,
  setMediaDetails,
  updateIsClearFilter,
  updateIsFilter,
  onEmbedUpdated,
  closeModal,
  onMediaMetaSave,
  setLoading,
  toast,
}: UseEmbedMediaOperationsProps) => {
  const getEmbedCreationErrorMessage = (resp: any) => {
    const rawError =
      resp?.error ||
      resp?.message ||
      resp?.data?.error ||
      resp?.data?.message ||
      "";
    const normalizedError = String(rawError).toLowerCase();
    if (normalizedError.includes("unique constraint failed")) {
      return "Embed Media Already Exists!";
    }
    return "";
  };

  const getPlatform = useCallback((embedType: string) => {
    return embedType === "0"
      ? "youtube"
      : embedType === "1"
        ? "facebook"
        : "hls";
  }, []);

  const handleMediaUpdate = useCallback(
    async (embedId: string, platform: string) => {
      getUpdateMedia(
        {
          ...currentMediaDetailsForEdit,
          embed: { id: embedId, type: platform },
          youtube_id: embedId,
          islive: formik.values.Streaming === "Live" ? 1 : 0,
        },
        true
      );

      if (onEmbedUpdated) {
        onEmbedUpdated({ id: embedId });
      }
      closeModal();
    },
    [
      currentMediaDetailsForEdit,
      formik.values.Streaming,
      getUpdateMedia,
      onEmbedUpdated,
      closeModal,
    ]
  );

  const handleMediaCreation = useCallback(
    async (data: any, embedId: string, platform: string) => {
      const mediaData = {
        type: platform,
        data,
        fid: data?.id,
        name: "media",
        title:
          data?.title || data?.desc?.trim()?.slice(0, 100) || "Untitled Media",
        source: "embed",
        islive: formik.values.Streaming === "Live" ? 1 : 0,
        embed: { id: embedId, type: platform },
        asset_type: formik.values.AssetType,
        metaa: {
          type: formik.values.AssetType,
          video: formik.values.AssetType === "video",
          audio: true,
        },
      };

      callRegisterCustomMediaOnCms(mediaData, (resp: any) => {
        updateIsClearFilter(true);
        updateIsFilter(false);

        if (resp.status === 200) {
          setMediaDetails({ data: { media: resp.data } });
          if (onMediaMetaSave) {
            onMediaMetaSave(resp.data.id);
          }
        } else {
          if (setLoading) {
            setLoading(false);
          }
          if (toast) {
            toast.fail(getEmbedCreationErrorMessage(resp));
          }
        }
      });
    },
    [
      formik.values.Streaming,
      formik.values.AssetType,
      callRegisterCustomMediaOnCms,
      updateIsClearFilter,
      updateIsFilter,
      setMediaDetails,
      onMediaMetaSave,
      setLoading,
      toast,
    ]
  );

  const handleFormSubmit = useCallback(
    (mediaMetadata: any) => {
      const platform = getPlatform(formik.values.EmbedType);
      if (!mediaMetadata) {
        return;
      }
      // Clean URL when an <iframe> embed snippet was pasted.
      const normalizedEmbedUrl = extractUrlFromEmbed(formik.values.Embed_Url);
      const finalEmbedId =
        platform === "youtube" || platform === "facebook"
          ? mediaMetadata.id
          : normalizedEmbedUrl;

      if (setLoading) {
        setLoading(true);
      }

      if (fromDetails) {
        getUpdateMedia(
          {
            ...currentMediaDetailsForEdit,
            embed: {
              id: finalEmbedId,
              type: platform,
            },
            youtube_id: finalEmbedId,
            islive: formik.values.Streaming === "Live" ? 1 : 0,
          },
          true
        );

        if (onEmbedUpdated) {
          onEmbedUpdated({
            id: finalEmbedId,
          });
        }
        closeModal();
      } else {
        callRegisterCustomMediaOnCms(
          {
            type: platform,
            fid:
              platform === "hls" ? btoa(mediaMetadata?.id) : mediaMetadata?.id,
            data: mediaMetadata,
            name: "media",
            title:
              mediaMetadata?.title !== ""
                ? mediaMetadata?.title
                : mediaMetadata?.desc?.trim()?.slice(0, 100),
            source: "embed",
            islive: formik.values.Streaming === "Live" ? 1 : 0,
            embed: {
              id: finalEmbedId,
              type: platform,
            },
            asset_type: formik.values.AssetType,
            metaa: {
              type: formik.values.AssetType,
              video: formik.values.AssetType === "video" ? true : false,
              audio: true,
            },
          },
          (resp: any) => {
            if (resp.status === 200) {
              setMediaDetails({
                data: {
                  media: resp.data,
                },
              });
              if (onMediaMetaSave) {
                onMediaMetaSave(resp.data.id);
              }
            } else {
              if (setLoading) {
                setLoading(false);
              }
              if (toast) {
                toast.fail(getEmbedCreationErrorMessage(resp));
              }
            }
          }
        );
      }
    },
    [
      formik.values.EmbedType,
      formik.values.Embed_Url,
      formik.values.Streaming,
      formik.values.AssetType,
      fromDetails,
      currentMediaDetailsForEdit,
      getUpdateMedia,
      callRegisterCustomMediaOnCms,
      setMediaDetails,
      onEmbedUpdated,
      onMediaMetaSave,
      closeModal,
      setLoading,
      getPlatform,
      toast,
    ]
  );

  return {
    handleMediaUpdate,
    handleMediaCreation,
    handleFormSubmit,
    getPlatform,
  };
};
